import makeStyles from '@mui/styles/makeStyles'
import { getBadgeColors } from './colors'

/*
 * Neutral chrome (hairlines, body copy) reads from theme tokens — the grey shades
 * map 1:1 onto the original hex values (shade[10] #DDDBE0, shade[90] #313335,
 * shade[100] #18191A). Brand colours (seal surface, chip tint/text) derive from
 * the tenant brand via `getBadgeColors`, which keeps foregrounds accessible on
 * any brand colour — see colors.js.
 */
/*
 * Copy metrics. The two paragraphs are sized so the block's *optical* height —
 * cap-height of the first line to baseline of the last — matches the 88px seal,
 * which is what keeps the copy's top and bottom aligned with the logo.
 *
 * Raising the leading inflates every line box, including a half-leading strip
 * above the first line and below the last that would push the visible copy
 * inward and break that alignment. `HALF_LEADING_TRIM` removes exactly those two
 * strips ((1 - LINE_HEIGHT) / 2 em on each side), so the extra air lands only
 * *between* the lines: 4 lines at 1.5 measure 96px, less the 4.1px and 3.9px
 * trims, is the same 88px the 1.35 leading produced — with ~43% more space
 * between lines. Adjust LINE_HEIGHT alone; the trim follows it.
 */
const STATEMENT_SIZE = 16.5
const DETAILS_SIZE = 15.5
const LINE_HEIGHT = 1.5
const HALF_LEADING_TRIM = `${(1 - LINE_HEIGHT) / 2}em`

/*
 * Every rule here is static: the banner's two placement switches (see index.js)
 * are expressed as modifier classes rather than as a dynamic `props => ({...})`
 * rule, so @mui/styles attaches one shared sheet instead of a per-instance one.
 */
export const useStyles = makeStyles(({ palette, spacing, typography, breakpoints }) => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2.75),
    marginTop: spacing(4),
    padding: spacing(2.5, 0.5),
    borderTop: `1px solid ${palette.grey.shade[10]}`,
    borderBottom: `1px solid ${palette.grey.shade[10]}`,
    // On phones the row cramps the copy against the 88px seal — stack instead.
    [breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: spacing(1.5)
    }
  },
  // Above-the-footer placements drop the bottom hairline — the footer edge already closes the block.
  bannerNoBottomDivider: {
    borderBottom: 'none'
  },
  // Placements whose parent already spaces its children (a flex `gap`, a grid
  // gutter) drop the banner's own top margin rather than stacking on it.
  bannerNoTopSpacing: {
    marginTop: 0
  },
  sealWrap: {
    flex: '0 0 auto',
    display: 'flex'
  },
  statement: {
    fontWeight: typography.weight.bold,
    fontSize: STATEMENT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: palette.grey.shade[100],
    // Trim the half-leading above the first line — see LINE_HEIGHT.
    '&::before': { content: '""', display: 'block', marginTop: HALF_LEADING_TRIM }
  },
  details: {
    fontStyle: 'italic',
    fontSize: DETAILS_SIZE,
    lineHeight: LINE_HEIGHT,
    color: palette.grey.shade[90],
    // ...and below the last line, so the block ends flush with the seal's base.
    '&::after': { content: '""', display: 'block', marginBottom: HALF_LEADING_TRIM }
  },
  detailsLink: {
    color: 'inherit',
    textDecoration: 'underline',
    touchAction: 'manipulation',
    // Inline link is only ~21px tall — pad the hit area to ~44px on touch,
    // negative margin keeps it flowing inside the sentence.
    [breakpoints.down('sm')]: {
      display: 'inline-block',
      paddingBlock: spacing(0.75),
      marginBlock: spacing(-0.75)
    }
  }
}))

// Kept separate from the banner sheet so a chip does not carry the banner's rules.
export const useChipStyles = makeStyles(({ palette, spacing, typography, breakpoints }) => {
  const badge = getBadgeColors(palette)

  return {
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: spacing(0.875),
      padding: spacing(0.625, 1.5, 0.625, 0.75),
      borderRadius: 9999,
      background: badge.chipBg,
      color: badge.chipFg,
      fontWeight: typography.weight.semiBold,
      fontSize: 12.5,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      maxWidth: '100%',
      textDecoration: 'none',
      cursor: 'pointer',
      touchAction: 'manipulation',
      // Whole pill is the link — guarantee a 44px hit height on touch without
      // enlarging its visual footprint on desktop.
      [breakpoints.down('sm')]: {
        minHeight: 44,
        paddingBlock: spacing(1),
        // The label names all three steps ("researched, written and reviewed"),
        // which at ~42 chars — and ~50 in de/nl/tr — overruns a phone-width
        // byline column. Wrap rather than truncate: the full wording is the
        // point of the chip.
        whiteSpace: 'normal',
        lineHeight: 1.3
      }
    },
    chipDisc: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 22,
      height: 22,
      borderRadius: 9999,
      background: badge.discBg,
      color: badge.discFg,
      flex: '0 0 auto'
    }
  }
})
